import { useEffect, useState } from 'react';
import { Admin } from '../../api.js';
import { FormField } from '../../components/FormField.jsx';
import { PageHeader } from '../../components/PageHeader.jsx';
import { Modal, ConfirmDialog, submitForm } from '../../components/Modal.jsx';

const chipFor = {
  pending: 'chip-warn',
  approved: 'chip-brand',
  paid: 'chip-success',
  rejected: 'chip-danger',
  cancelled: 'chip-neutral',
};

const emptyNew = {
  name: '',
  email: '',
  phone: '',
  start: '',
  end: '',
  purpose: '',
  guestCount: 20,
  notes: '',
  priceCents: '',
  status: 'paid',
  adminNotes: '',
};

export default function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState('');
  const [approving, setApproving] = useState(null);
  const [price, setPrice] = useState('500');
  const [notes, setNotes] = useState('');
  const [rejecting, setRejecting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newForm, setNewForm] = useState(emptyNew);
  const [err, setErr] = useState(null);
  const [payLink, setPayLink] = useState(null);
  const [payPrice, setPayPrice] = useState('');
  const [paySendEmail, setPaySendEmail] = useState(true);

  async function refresh() {
    const r = await Admin.bookings.list(filter || undefined);
    setBookings(r.bookings);
  }
  useEffect(() => { refresh(); }, [filter]);

  async function doApprove() {
    const cents = Math.round(Number(price) * 100);
    if (!Number.isFinite(cents) || cents <= 0) return;
    setBusy(true);
    try {
      await Admin.bookings.approve(approving.id, { priceCents: cents, adminNotes: notes || undefined });
      setApproving(null); setPrice('500'); setNotes('');
      await refresh();
    } finally { setBusy(false); }
  }

  function openCreate() {
    setNewForm(emptyNew);
    setErr(null);
    setCreating(true);
  }

  function openPaymentLink(b) {
    setPayLink(b);
    setPayPrice(b.priceCents ? (b.priceCents / 100).toFixed(2) : '');
    setPaySendEmail(true);
    setErr(null);
  }

  async function doPaymentLink() {
    const cents = Math.round(Number(payPrice) * 100);
    if (!Number.isFinite(cents) || cents <= 0) return setErr('Enter a valid price.');
    setBusy(true);
    setErr(null);
    try {
      await Admin.bookings.paymentLink(payLink.id, { priceCents: cents, sendEmail: paySendEmail });
      setPayLink(null);
      await refresh();
    } catch (e) {
      setErr(e.data?.issues ? e.data.issues.map((i) => i.message).join(', ') : e.message);
    } finally { setBusy(false); }
  }

  async function doCreate(e) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const payload = {
        name: newForm.name,
        email: newForm.email,
        phone: newForm.phone || undefined,
        purpose: newForm.purpose,
        guestCount: Number(newForm.guestCount),
        notes: newForm.notes || undefined,
        start: new Date(newForm.start).toISOString(),
        end: new Date(newForm.end).toISOString(),
        status: newForm.status,
        priceCents: newForm.priceCents === '' ? undefined : Math.round(Number(newForm.priceCents) * 100),
        adminNotes: newForm.adminNotes || undefined,
      };
      await Admin.bookings.create(payload);
      setCreating(false);
      await refresh();
    } catch (e) {
      setErr(e.data?.issues ? e.data.issues.map((i) => i.message).join(', ') : e.message);
    } finally { setBusy(false); }
  }

  return (
    <div>
      <PageHeader
        eyebrow="Clubhouse"
        title="Rental bookings"
        description="Review inquiries, quote a price, and send a payment link — or create a booking directly."
        action={<button onClick={openCreate} className="btn-primary btn-sm">+ New booking</button>}
      />
      <div className="flex items-center gap-3 mb-5">
        <select className="input max-w-xs" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved (awaiting payment)</option>
          <option value="paid">Paid</option>
          <option value="rejected">Rejected</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <span className="text-xs text-ink-3">{bookings.length} {bookings.length === 1 ? 'booking' : 'bookings'}</span>
      </div>

      <div className="space-y-3">
        {bookings.map((b) => (
          <article key={b.id} className="card">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <h3 className="font-display text-xl">{b.purpose}</h3>
                  <span className={chipFor[b.status] || 'chip-neutral'}>{b.status}</span>
                </div>
                <div className="text-sm text-ink-2">
                  <span className="font-medium text-ink">{fmtRange(b.start, b.end)}</span>
                </div>
                <div className="text-sm text-ink-2 mt-1">
                  {b.name} · {b.email}{b.phone ? ` · ${b.phone}` : ''} · {b.guestCount} guests
                </div>
                {b.notes && <p className="text-sm text-ink-3 mt-3 italic">"{b.notes}"</p>}
                {b.stripePaymentLinkUrl && (
                  <div className="mt-3 flex items-center gap-3 flex-wrap">
                    <a href={b.stripePaymentLinkUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-brand underline underline-offset-4">
                      Payment link →
                    </a>
                    <button
                      className="text-[11px] text-ink-3 hover:text-ink"
                      onClick={() => {
                        navigator.clipboard?.writeText(b.stripePaymentLinkUrl);
                      }}
                    >
                      Copy
                    </button>
                  </div>
                )}
              </div>
              <div className="flex md:flex-col gap-2 shrink-0">
                {b.status === 'pending' && (
                  <>
                    <button className="btn-primary btn-sm" onClick={() => setApproving(b)}>Approve & quote</button>
                    <button className="btn-outline btn-sm text-danger" onClick={() => setRejecting(b)}>Reject</button>
                  </>
                )}
                {(b.status === 'approved' || (b.status === 'paid' && !b.stripePaymentLinkUrl)) && (
                  <button className="btn-primary btn-sm" onClick={() => openPaymentLink(b)}>
                    {b.stripePaymentLinkUrl ? 'New payment link' : 'Create payment link'}
                  </button>
                )}
                {b.status === 'paid' && b.stripePaymentLinkUrl && (
                  <button className="btn-outline btn-sm" onClick={() => openPaymentLink(b)}>
                    Regenerate link
                  </button>
                )}
              </div>
            </div>
          </article>
        ))}
        {!bookings.length && <div className="card text-center text-ink-3 italic font-display">No bookings.</div>}
      </div>

      <Modal
        open={!!approving}
        onClose={() => setApproving(null)}
        eyebrow="Approve"
        title={approving?.purpose}
        footer={
          <>
            <button onClick={() => setApproving(null)} className="btn-ghost">Cancel</button>
            <button onClick={doApprove} className="btn-primary" disabled={busy}>{busy ? 'Creating link…' : 'Approve & email link'}</button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="card bg-line-soft/40 border-0 text-sm">
            <div className="font-medium">{approving?.name}</div>
            <div className="text-ink-2">{approving && fmtRange(approving.start, approving.end)}</div>
            <div className="text-ink-2">{approving?.guestCount} guests · {approving?.email}</div>
          </div>
          <FormField label="Quote (USD)" required>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-3 text-sm">$</span>
              <input type="number" min="1" step="1" className="input pl-7" value={price} onChange={(e) => setPrice(e.target.value)} required autoFocus />
            </div>
          </FormField>
          <FormField label="Notes for the requester" hint="Optional — will be saved but not emailed">
            <textarea className="input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </FormField>
          <p className="text-xs text-ink-3">
            Creates a Stripe payment link and emails it to <strong>{approving?.email}</strong>. The booking is confirmed on payment.
          </p>
        </div>
      </Modal>

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        eyebrow="New"
        title="Create booking"
        size="lg"
        footer={
          <>
            <button type="button" onClick={() => setCreating(false)} className="btn-ghost">Cancel</button>
            <button type="button" onClick={() => submitForm('new-booking-form')} className="btn-primary" disabled={busy}>
              {busy ? 'Creating…' : 'Create booking'}
            </button>
          </>
        }
      >
        {err && <div className="mb-4 px-3 py-2 rounded-md bg-danger/10 text-danger text-sm">{err}</div>}
        <form id="new-booking-form" onSubmit={doCreate} className="space-y-4">
          <div className="eyebrow pb-2 border-b border-line">Requester</div>
          <FormField label="Name" required>
            <input className="input" value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })} required autoFocus />
          </FormField>
          <div className="grid sm:grid-cols-2 gap-4">
            <FormField label="Email" required>
              <input type="email" className="input" value={newForm.email} onChange={(e) => setNewForm({ ...newForm, email: e.target.value })} required />
            </FormField>
            <FormField label="Phone">
              <input className="input" value={newForm.phone} onChange={(e) => setNewForm({ ...newForm, phone: e.target.value })} />
            </FormField>
          </div>

          <div className="eyebrow pt-3 pb-2 border-b border-line">When</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <FormField label="Start" required>
              <input type="datetime-local" className="input" value={newForm.start} onChange={(e) => setNewForm({ ...newForm, start: e.target.value })} required />
            </FormField>
            <FormField label="End" required>
              <input type="datetime-local" className="input" value={newForm.end} onChange={(e) => setNewForm({ ...newForm, end: e.target.value })} required />
            </FormField>
          </div>

          <div className="eyebrow pt-3 pb-2 border-b border-line">Details</div>
          <FormField label="Purpose" required>
            <input className="input" value={newForm.purpose} onChange={(e) => setNewForm({ ...newForm, purpose: e.target.value })} required />
          </FormField>
          <div className="grid sm:grid-cols-2 gap-4">
            <FormField label="Guests" required>
              <input type="number" min="1" max="300" className="input" value={newForm.guestCount} onChange={(e) => setNewForm({ ...newForm, guestCount: e.target.value })} required />
            </FormField>
            <FormField label="Price (USD)" hint="Optional — for your records">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-3 text-sm">$</span>
                <input type="number" min="0" step="0.01" className="input pl-7" value={newForm.priceCents} onChange={(e) => setNewForm({ ...newForm, priceCents: e.target.value })} />
              </div>
            </FormField>
          </div>
          <FormField label="Status">
            <select className="input" value={newForm.status} onChange={(e) => setNewForm({ ...newForm, status: e.target.value })}>
              <option value="paid">Paid (confirmed)</option>
              <option value="approved">Approved (awaiting payment)</option>
            </select>
          </FormField>
          <FormField label="Notes" hint="Guest-facing — catering, AV, etc.">
            <textarea className="input" rows={2} value={newForm.notes} onChange={(e) => setNewForm({ ...newForm, notes: e.target.value })} />
          </FormField>
          <FormField label="Admin notes" hint="Internal only">
            <textarea className="input" rows={2} value={newForm.adminNotes} onChange={(e) => setNewForm({ ...newForm, adminNotes: e.target.value })} />
          </FormField>
          <p className="text-[11px] text-ink-3 pt-2 border-t border-line">
            Creates a booking and a calendar event that blocks the slot immediately. No Stripe payment link is generated — use the Approve flow for that.
          </p>
        </form>
      </Modal>

      <Modal
        open={!!payLink}
        onClose={() => setPayLink(null)}
        eyebrow="Payment"
        title={payLink ? `${payLink.purpose} — ${payLink.name}` : ''}
        footer={
          <>
            <button type="button" onClick={() => setPayLink(null)} className="btn-ghost">Cancel</button>
            <button type="button" onClick={doPaymentLink} className="btn-primary" disabled={busy}>
              {busy ? 'Creating link…' : paySendEmail ? 'Create & email link' : 'Create link'}
            </button>
          </>
        }
      >
        {err && <div className="mb-4 px-3 py-2 rounded-md bg-danger/10 text-danger text-sm">{err}</div>}
        <div className="space-y-4">
          {payLink && (
            <div className="card bg-line-soft/40 border-0 text-sm">
              <div className="font-medium">{payLink.name}</div>
              <div className="text-ink-2">{payLink.email}</div>
              <div className="text-ink-2 mt-1">
                {new Date(payLink.start).toLocaleString()} → {new Date(payLink.end).toLocaleTimeString()}
              </div>
              <div className="text-ink-2">{payLink.guestCount} guests</div>
            </div>
          )}
          <FormField label="Amount (USD)" required>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-3 text-sm">$</span>
              <input type="number" min="0" step="0.01" className="input pl-7" value={payPrice} onChange={(e) => setPayPrice(e.target.value)} autoFocus required />
            </div>
          </FormField>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="check mt-0.5" checked={paySendEmail} onChange={(e) => setPaySendEmail(e.target.checked)} />
            <span>
              Email the link to <strong>{payLink?.email}</strong>
              <span className="block text-[11px] text-ink-3 mt-0.5">
                Uncheck to just generate the URL for copying
              </span>
            </span>
          </label>
          <p className="text-[11px] text-ink-3 pt-3 border-t border-line">
            Creates a Stripe-hosted payment page. When paid, the webhook marks this booking as paid automatically.
          </p>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!rejecting}
        onClose={() => setRejecting(null)}
        onConfirm={async () => {
          await Admin.bookings.reject(rejecting.id);
          await refresh();
        }}
        title="Reject inquiry?"
        message="The requester is not notified automatically — reach out personally if appropriate."
        confirmLabel="Reject"
        danger
      />
    </div>
  );
}

function fmtRange(s, e) {
  const start = new Date(s);
  const end = new Date(e);
  const opts = { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
  return `${start.toLocaleString(undefined, opts)} → ${end.toLocaleString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
}
